import { gql } from "@apollo/client/core";
import { apolloClient } from "../graphql/client";
import { useAuthStore } from "./auth-store";
import type { AuthPayload } from "./auth-store";

// GraphQL mutations
const REFRESH_TOKENS_MUTATION = gql`
  mutation RefreshTokens($input: RefreshTokensInput!) {
    refreshTokens(input: $input) {
      accessToken
      refreshToken
      tokenType
      expiresIn
      user {
        id
        email
        name
      }
    }
  }
`;

const SIGN_OUT_MUTATION = gql`
  mutation SignOut($input: SignOutInput!) {
    signOut(input: $input) {
      success
    }
  }
`;

/** Get the current access token (or null if not authenticated) */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

/** Get the current refresh token (or null if not authenticated) */
export function getRefreshToken(): string | null {
  return useAuthStore.getState().refreshToken;
}

/** Check if user is currently authenticated (has a non-null access token) */
export function isAuthenticated(): boolean {
  return useAuthStore.getState().isAuthenticated;
}

/**
 * Attempt silent token refresh.
 * - Calls the refreshTokens GraphQL mutation with the current refresh token
 * - On success: updates auth store with new tokens, returns true
 * - On failure: calls signOut(), returns false
 * - Thread-safe: only one refresh can be in-flight at a time (debounced via isRefreshing flag)
 */
export async function silentRefresh(): Promise<boolean> {
  const store = useAuthStore.getState();

  // Don't double-refresh
  if (store.isRefreshing) {
    return false;
  }

  const refreshToken = store.refreshToken;
  if (!refreshToken) {
    await handleSignOut();
    return false;
  }

  store.setRefreshing(true);

  try {
    const result = await apolloClient.mutate({
      mutation: REFRESH_TOKENS_MUTATION,
      variables: { input: { refreshToken } },
    });

    const data = result.data as {
      refreshTokens?: {
        accessToken: string;
        refreshToken: string;
        tokenType: string;
        expiresIn: number;
        user: { id: string; email: string; name: string | null };
      };
    } | null;

    if (data?.refreshTokens) {
      useAuthStore
        .getState()
        .updateTokens(data.refreshTokens.accessToken, data.refreshTokens.refreshToken);
      return true;
    }

    // Refresh returned no data — sign out
    await handleSignOut();
    return false;
  } catch {
    // Refresh failed — sign out
    await handleSignOut();
    return false;
  } finally {
    useAuthStore.getState().setRefreshing(false);
  }
}

/**
 * Handle sign-in with auth payload from magic link or OAuth exchange.
 * - Stores access token, refresh token, and user in auth store
 * - Auth store persistence middleware writes to localStorage
 */
export function handleSignIn(payload: AuthPayload): void {
  useAuthStore.getState().signIn(payload);
}

/**
 * Handle sign-out.
 * - Calls signOut mutation on backend (best-effort, don't block on failure)
 * - Clears auth store (removes tokens and user)
 * - Resets Apollo Client cache
 * - Navigates to /sign-in
 */
export async function handleSignOut(): Promise<void> {
  // Best-effort backend sign-out
  try {
    const refreshToken = useAuthStore.getState().refreshToken;
    await apolloClient.mutate({
      mutation: SIGN_OUT_MUTATION,
      variables: { input: { refreshToken, revokeAll: false } },
    });
  } catch {
    // Don't block sign-out on backend failure
  }

  // Clear client state
  useAuthStore.getState().signOut();

  // Reset Apollo cache
  try {
    await apolloClient.clearStore();
  } catch {
    // Ignore cache clear errors
  }

  // Navigate to sign-in (if we have access to window)
  if (typeof window !== "undefined") {
    window.location.href = "/sign-in";
  }
}

/**
 * Validate stored token data on app startup.
 * - Called during app initialization (main.tsx)
 * - Checks if localStorage has parseable auth data
 * - If corrupt/invalid: clears store and returns false
 * - If valid tokens exist: returns true (does NOT validate JWT expiry — that's handled by the API)
 */
export function validateStoredAuth(): boolean {
  try {
    const stored = localStorage.getItem("auth-storage");
    if (!stored) {
      return false;
    }

    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== "object" || parsed === null) {
      localStorage.removeItem("auth-storage");
      useAuthStore.getState().signOut();
      return false;
    }

    const state = (parsed as Record<string, unknown>)["state"];
    if (typeof state !== "object" || state === null) {
      localStorage.removeItem("auth-storage");
      useAuthStore.getState().signOut();
      return false;
    }

    const accessToken = (state as Record<string, unknown>)["accessToken"];
    const refreshToken = (state as Record<string, unknown>)["refreshToken"];

    if (
      typeof accessToken === "string" &&
      accessToken.length > 0 &&
      typeof refreshToken === "string" &&
      refreshToken.length > 0
    ) {
      return true;
    }

    // Invalid token data
    useAuthStore.getState().signOut();
    return false;
  } catch {
    // Corrupt JSON — clear and return false
    try {
      localStorage.removeItem("auth-storage");
    } catch {
      // Ignore
    }
    useAuthStore.getState().signOut();
    return false;
  }
}
